import type { Graph, Edge, Node } from '@antv/x6';
import { applyInvocationZigzagToEdge } from './invocationZigzag';

type EdgeRoutingMeta = {
  _mergedEdge?: boolean;
  _originalConnector?: any;
  _originalRouter?: any;
  edgeSubtype?: string;
  edgeCategory?: string;
  isOptionArrowEdge?: boolean;
  _cachedSourceDir?: EdgeDirection;
  _cachedTargetDir?: EdgeDirection;
  _parentAttachSide?: EdgeDirection;
  _parentAttachRatio?: number;
};

type EdgeDirection = 'top' | 'bottom' | 'left' | 'right';

const OPTION_ARROW_ELBOW = 26;
const OPTION_ARROW_APPROACH = 16;
const OPTION_ARROW_VERTICAL_GAP = 0;
const DEFAULT_ROUTER_PADDING = 20;
const STUB_LENGTH = 16;

const getNodeCenter = (node: Node) => {
  const pos = node.getPosition();
  const size = node.getSize();
  return {
    x: pos.x + size.width / 2,
    y: pos.y + size.height / 2,
  };
};

const pickDirectionFacingSource = (): EdgeDirection => 'top';

const pickOptionArrowTargetDir = (
  sourceNode: Node,
  targetNode: Node,
): EdgeDirection => {
  const sCenter = getNodeCenter(sourceNode);
  const tCenter = getNodeCenter(targetNode);
  const dx = sCenter.x - tCenter.x;
  const dy = sCenter.y - tCenter.y;
  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx > 0 ? 'right' : 'left';
  }
  return dy > 0 ? 'bottom' : 'top';
};

const pickBestDirections = (
  sourceNode: Node,
  targetNode: Node,
): { sourceDir: EdgeDirection; targetDir: EdgeDirection } => {
  const sPos = sourceNode.getPosition();
  const sSize = sourceNode.getSize();
  const tPos = targetNode.getPosition();
  const tSize = targetNode.getSize();
  const padding = 12;

  const sLeft = sPos.x;
  const sRight = sPos.x + sSize.width;
  const sTop = sPos.y;
  const sBottom = sPos.y + sSize.height;
  const tLeft = tPos.x;
  const tRight = tPos.x + tSize.width;
  const tTop = tPos.y;
  const tBottom = tPos.y + tSize.height;

  if (sRight + padding <= tLeft) {
    return { sourceDir: 'right', targetDir: 'left' };
  }
  if (tRight + padding <= sLeft) {
    return { sourceDir: 'left', targetDir: 'right' };
  }
  if (sBottom + padding <= tTop) {
    return { sourceDir: 'bottom', targetDir: 'top' };
  }
  if (tBottom + padding <= sTop) {
    return { sourceDir: 'top', targetDir: 'bottom' };
  }

  const sCenter = getNodeCenter(sourceNode);
  const tCenter = getNodeCenter(targetNode);
  const dx = tCenter.x - sCenter.x;
  const dy = tCenter.y - sCenter.y;
  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0
      ? { sourceDir: 'right', targetDir: 'left' }
      : { sourceDir: 'left', targetDir: 'right' };
  }
  return dy >= 0
    ? { sourceDir: 'bottom', targetDir: 'top' }
    : { sourceDir: 'top', targetDir: 'bottom' };
};

const getBoundaryPoint = (node: Node, dir: EdgeDirection) => {
  const pos = node.getPosition();
  const size = node.getSize();
  const cx = pos.x + size.width / 2;
  const cy = pos.y + size.height / 2;
  switch (dir) {
    case 'top':
      return { x: cx, y: pos.y };
    case 'bottom':
      return { x: cx, y: pos.y + size.height };
    case 'left':
      return { x: pos.x, y: cy };
    case 'right':
    default:
      return { x: pos.x + size.width, y: cy };
  }
};

export const toParentAttach = (
  parentNode: Node,
  point: { x: number; y: number },
): { side: EdgeDirection; ratio: number } => {
  const bbox = parentNode.getBBox();
  const leftGap = Math.abs(point.x - bbox.x);
  const rightGap = Math.abs(point.x - (bbox.x + bbox.width));
  const topGap = Math.abs(point.y - bbox.y);
  const bottomGap = Math.abs(point.y - (bbox.y + bbox.height));
  const minGap = Math.min(leftGap, rightGap, topGap, bottomGap);

  if (minGap === topGap) {
    const ratio = clamp((point.x - bbox.x) / Math.max(1, bbox.width), 0, 1);
    return { side: 'top', ratio };
  }
  if (minGap === bottomGap) {
    const ratio = clamp((point.x - bbox.x) / Math.max(1, bbox.width), 0, 1);
    return { side: 'bottom', ratio };
  }
  if (minGap === leftGap) {
    const ratio = clamp((point.y - bbox.y) / Math.max(1, bbox.height), 0, 1);
    return { side: 'left', ratio };
  }
  const ratio = clamp((point.y - bbox.y) / Math.max(1, bbox.height), 0, 1);
  return { side: 'right', ratio };
};

export const pointFromParentAttach = (
  parentNode: Node,
  side: EdgeDirection,
  ratio: number,
) => {
  const bbox = parentNode.getBBox();
  const r = clamp(ratio, 0, 1);
  let pt: { x: number; y: number };
  if (side === 'top') pt = { x: bbox.x + bbox.width * r, y: bbox.y };
  else if (side === 'bottom') pt = { x: bbox.x + bbox.width * r, y: bbox.y + bbox.height };
  else if (side === 'left') pt = { x: bbox.x, y: bbox.y + bbox.height * r };
  else pt = { x: bbox.x + bbox.width, y: bbox.y + bbox.height * r };

  // 椭圆节点：将矩形边界点投影到椭圆边界上
  const isEllipse = parentNode.shape === 'opm-process';
  if (isEllipse) {
    const cx = bbox.x + bbox.width / 2;
    const cy = bbox.y + bbox.height / 2;
    const hw = bbox.width / 2;
    const hh = bbox.height / 2;
    if (hw > 0 && hh > 0) {
      const dx = pt.x - cx;
      const dy = pt.y - cy;
      if (dx !== 0 || dy !== 0) {
        const angle = Math.atan2(dy, dx);
        pt = {
          x: cx + hw * Math.cos(angle),
          y: cy + hh * Math.sin(angle),
        };
      }
    }
  }
  return pt;
};

const buildStubVertices = (
  sourceNode: Node,
  targetNode: Node,
  sourceDir: EdgeDirection,
  targetDir: EdgeDirection,
): { x: number; y: number }[] => {
  const sourceBoundary = getBoundaryPoint(sourceNode, sourceDir);
  const targetBoundary = getBoundaryPoint(targetNode, targetDir);
  const sourceStub = shiftPoint(sourceBoundary, sourceDir, STUB_LENGTH);
  const targetStub = shiftPoint(targetBoundary, targetDir, STUB_LENGTH);
  const sourceIsVertical = sourceDir === 'top' || sourceDir === 'bottom';
  const targetIsVertical = targetDir === 'top' || targetDir === 'bottom';

  const firstCorner = sourceIsVertical
    ? { x: targetBoundary.x, y: sourceStub.y }
    : { x: sourceStub.x, y: targetBoundary.y };
  const secondCorner = targetIsVertical
    ? { x: targetStub.x, y: firstCorner.y }
    : { x: firstCorner.x, y: targetStub.y };

  const vertices: { x: number; y: number }[] = [sourceStub];
  const pushIfNeeded = (pt: { x: number; y: number }) => {
    const last = vertices[vertices.length - 1];
    if (!last || Math.abs(last.x - pt.x) > 0.5 || Math.abs(last.y - pt.y) > 0.5) {
      vertices.push(pt);
    }
  };

  pushIfNeeded(firstCorner);
  pushIfNeeded(secondCorner);
  pushIfNeeded(targetStub);

  return vertices;
};

export const buildOptionArrowVertices = (
  graph: Graph,
  edge: Edge,
  targetApproachDir?: EdgeDirection,
) => {
  if (!isOptionArrowEdge(edge)) return null;
  const sourceId = edge.getSourceCellId?.();
  const targetId = edge.getTargetCellId?.();
  if (!sourceId || !targetId) return null;
  const sourceNode = graph.getCellById(sourceId) as Node | null;
  const targetNode = graph.getCellById(targetId) as Node | null;
  if (!sourceNode || !targetNode || !sourceNode.isNode?.() || !targetNode.isNode?.()) return null;
  const boundaryPoint = getBoundaryPoint(sourceNode, 'top');
  const elbow = shiftPoint(boundaryPoint, 'top', OPTION_ARROW_ELBOW);
  const direction = targetApproachDir || pickOptionArrowTargetDir(sourceNode, targetNode);
  const targetBoundary = getBoundaryPoint(targetNode, direction);
  const arrowStartPoint = shiftPoint(targetBoundary, direction, OPTION_ARROW_APPROACH);
  const targetX = arrowStartPoint.x;
  const targetY = arrowStartPoint.y;
  const vertices = [elbow];
  if (Math.abs(targetX - elbow.x) > 1) {
    vertices.push({ x: targetX, y: elbow.y });
  }
  const last = vertices[vertices.length - 1];
  if (Math.abs(targetY - last.y) > 1) {
    vertices.push({ x: targetX, y: targetY });
  }
  return vertices;
};

const shiftPoint = (pt: { x: number; y: number }, dir: EdgeDirection, delta: number) => {
  switch (dir) {
    case 'top':
      return { x: pt.x, y: pt.y - delta };
    case 'bottom':
      return { x: pt.x, y: pt.y + delta };
    case 'left':
      return { x: pt.x - delta, y: pt.y };
    case 'right':
    default:
      return { x: pt.x + delta, y: pt.y };
  }
};

const isOptionArrowEdge = (edge: Edge) => {
  const sourcePort = (edge.getSource?.() as any)?.port;
  const data = (edge.getData?.() || {}) as EdgeRoutingMeta;
  return sourcePort === 'port-arrow' || !!data.isOptionArrowEdge;
};

export const applySingleEdgeRouting = (
  graph: Graph,
  edge: Edge,
  anchorName: EdgeDirection,
  isOptionTarget: boolean,
  forceOptionVertices = true,
) => {
  const data = ensureRouteMetadata(edge);
  const targetId = edge.getTargetCellId();
  const sourceId = edge.getSourceCellId();
  const isOptionEdge = isOptionArrowEdge(edge);
  const sourceNode = sourceId ? (graph.getCellById(sourceId) as Node | null) : null;
  const targetNode = targetId ? (graph.getCellById(targetId) as Node | null) : null;
  const bestDirs = !isOptionEdge && sourceNode && targetNode
    ? pickBestDirections(sourceNode, targetNode)
    : null;
  const optionApproachDir: EdgeDirection = isOptionEdge && sourceNode && targetNode
    ? pickOptionArrowTargetDir(sourceNode, targetNode)
    : 'top';
  if (targetId) {
    if (isOptionEdge && isOptionTarget) {
      edge.setTarget({
        cell: targetId,
        port: 'port-arrow',
        anchor: { name: optionApproachDir },
        connectionPoint: { name: 'anchor' },
      });
    } else {
      edge.setTarget({ cell: targetId, connectionPoint: { name: 'boundary' } });
    }
  }
  if (sourceId) {
    if (isOptionEdge) {
      edge.setSource({
        cell: sourceId,
        port: 'port-arrow',
        anchor: { name: 'top' },
        connectionPoint: { name: 'anchor' },
      });
    } else {
      edge.setSource({ cell: sourceId });
    }
  }
  if (isOptionEdge && sourceNode && targetNode) {
    const srcBox = sourceNode.getBBox();
    const tgtBox = targetNode.getBBox();
    const srcCx = srcBox.x + srcBox.width / 2;
    const stubY = srcBox.y - 32;
    const tgtCx = tgtBox.x + tgtBox.width / 2;
    const vertices: { x: number; y: number }[] = [
      { x: srcCx, y: stubY },
      { x: tgtCx, y: stubY },
    ];
    edge.setVertices(vertices);
    edge.setRouter({ name: 'orth', args: { padding: 20 } });
    edge.setConnector({ name: 'jumpover', args: { size: 5 } });
  } else if (!isOptionEdge && bestDirs && sourceNode && targetNode) {
    // 使用 orth 路由器，让 X6 根据节点位置自动计算最优正交路径
    // 节点移动时 X6 会自动重算，无需手动维护 vertices
    edge.setVertices([]);
    edge.setRouter({
      name: 'orth',
      args: { padding: 20 },
    });
    edge.setConnector({ name: 'jumpover', args: { size: 5 } });
  } else {
    if (data._originalConnector) {
      edge.setConnector(data._originalConnector);
    }
    if (data._originalRouter) {
      edge.setRouter(data._originalRouter);
    }
  }
  edge.setData({
    ...data,
    _mergedEdge: true,
    ...(forceOptionVertices ? { isOptionArrowEdge: true } : {}),
  });
};

function ensureRouteMetadata(edge: Edge): EdgeRoutingMeta {
  const rawData = (edge.getData?.() || {}) as EdgeRoutingMeta;
  const data: EdgeRoutingMeta = { ...rawData };
  let changed = false;
  if (!data._originalConnector) {
    data._originalConnector = edge.getConnector?.() || { name: 'jumpover', args: { size: 5 } };
    changed = true;
  }
  if (!data._originalRouter) {
    data._originalRouter = edge.getRouter?.() || { name: 'orth', args: { padding: 20 } };
    changed = true;
  }
  if (changed) {
    edge.setData(data);
  }
  return data;
}

/**
 * 合并同类型边：当多条同类型（edgeSubtype）的边指向同一个目标节点时，
 * 在目标节点附近计算一个汇聚点，让所有边先经过该汇聚点再到达目标，
 * 从而形成"合并展示"的视觉效果（树状/总线状）。
 *
 * 合并策略：
 * 1. 按 (targetId, edgeSubtype) 分组
 * 2. 组内只有1条边时不处理
 * 3. 组内多条边时，在目标节点外侧计算汇聚点，
 *    所有边设置两个顶点：各自的源端出射点 + 共享汇聚点
 */
export function mergeSameTypeEdges(graph: Graph): void {
  const edges = graph.getEdges();

  // 按 (targetId, edgeSubtype) 分组
  const groups = new Map<string, Edge[]>();

  edges.forEach(edge => {
    const targetId = edge.getTargetCellId();
    if (!targetId) return;

    const data = ensureRouteMetadata(edge);
    const category = data.edgeCategory || (edge.getData?.() as any)?.edgeCategory;
    if (category !== 'structural') return;
    const subtype = rawEdgeSubtype(edge, data);
    if (!subtype) return;

    if (isOptionArrowEdge(edge)) {
      const targetNode = graph.getCellById(targetId) as Node | null;
      const isOptionTarget = targetNode?.getData?.()?.type === 'option';
      applySingleEdgeRouting(graph, edge, 'top', !!isOptionTarget, true);
      return;
    }

    // 跳过同父子节点间的边及子节点与父副本之间的边，不做合并路由
    const sourceNode = graph.getCellById(edge.getSourceCellId()!) as Node | null;
    const targetNode = graph.getCellById(targetId) as Node | null;
    if (sourceNode && targetNode) {
      const srcParent = sourceNode.getParent?.();
      const tgtParent = targetNode.getParent?.();
      // 子节点之间的互连
      if (srcParent && tgtParent && srcParent.id === tgtParent.id && srcParent.isNode?.()) {
        const parentType = (srcParent as Node).getData?.()?.type;
        if (parentType === 'process' && subtype === 'invocation') {
          applyInvocationZigzagToEdge(edge, graph, true);
        } else {
          layoutSiblingEdgeWithinParent(edge, sourceNode, targetNode, srcParent as Node);
        }
        if (data._mergedEdge) edge.setData({ ...data, _mergedEdge: false });
        return;
      }
      // 子节点与父副本之间的互连
      const srcData = sourceNode.getData?.() || {};
      const tgtData = targetNode.getData?.() || {};
      if (srcParent?.isNode?.() && tgtData.isParentCopy && srcParent.id === targetNode.id) {
        // source 是 target(父副本) 的子节点
        const parentType = tgtData.type;
        if (parentType === 'process' && subtype === 'invocation') {
          applyInvocationZigzagToEdge(edge, graph, true);
        } else {
          layoutChildToParentEdgeWithinParent(edge, sourceNode, targetNode);
        }
        if (data._mergedEdge) edge.setData({ ...data, _mergedEdge: false });
        return;
      }
      if (tgtParent?.isNode?.() && srcData.isParentCopy && tgtParent.id === sourceNode.id) {
        // target 是 source(父副本) 的子节点
        const parentType = srcData.type;
        if (parentType === 'process' && subtype === 'invocation') {
          applyInvocationZigzagToEdge(edge, graph, true);
        } else {
          layoutChildToParentEdgeWithinParent(edge, targetNode, sourceNode);
        }
        if (data._mergedEdge) edge.setData({ ...data, _mergedEdge: false });
        return;
      }
    }

    const key = `${targetId}::${subtype}`;
    const group = groups.get(key) || [];
    group.push(edge);
    groups.set(key, group);
  });

  groups.forEach(group => {
    if (!group.length) return;
    const optionArrowEdges = group.filter(isOptionArrowEdge);
    const remainingEdges = group.filter(edge => !isOptionArrowEdge(edge));

    const sampleEdge = (remainingEdges[0] || optionArrowEdges[0]);
    if (!sampleEdge) return;

    const targetNode = graph.getCellById(sampleEdge.getTargetCellId()!) as Node;
    if (!targetNode || !targetNode.isNode()) return;
    const isOptionTarget = targetNode.getData?.()?.type === 'option';

    optionArrowEdges.forEach(edge => {
      applySingleEdgeRouting(graph, edge, 'top', isOptionTarget);
    });

    if (!remainingEdges.length) {
      return;
    }

    const targetPos = targetNode.getPosition();
    const targetSize = targetNode.getSize();
    const targetCenter = {
      x: targetPos.x + targetSize.width / 2,
      y: targetPos.y + targetSize.height / 2,
    };

    // 计算所有源节点的中心，确定汇聚点方向
    const sourceCenters: { x: number; y: number }[] = [];
    remainingEdges.forEach(edge => {
      const sourceNode = graph.getCellById(edge.getSourceCellId()!) as Node;
      if (sourceNode && sourceNode.isNode()) {
        const sp = sourceNode.getPosition();
        const ss = sourceNode.getSize();
        sourceCenters.push({
          x: sp.x + ss.width / 2,
          y: sp.y + ss.height / 2,
        });
      }
    });

    if (sourceCenters.length === 0) return;

    // 计算源节点群的平均中心
    const avgSource = sourceCenters.reduce(
      (acc, c) => ({ x: acc.x + c.x, y: acc.y + c.y }),
      { x: 0, y: 0 },
    );
    avgSource.x /= sourceCenters.length;
    avgSource.y /= sourceCenters.length;

    const diffX = avgSource.x - targetCenter.x;
    const diffY = avgSource.y - targetCenter.y;

    const srcMinX = Math.min(...sourceCenters.map(c => c.x));
    const srcMaxX = Math.max(...sourceCenters.map(c => c.x));
    const srcMinY = Math.min(...sourceCenters.map(c => c.y));
    const srcMaxY = Math.max(...sourceCenters.map(c => c.y));
    const margin = 20;
    const targetOutsideVertical = targetCenter.y < srcMinY - margin || targetCenter.y > srcMaxY + margin;
    const targetOutsideHorizontal = targetCenter.x < srcMinX - margin || targetCenter.x > srcMaxX + margin;

    let useHorizontal: boolean;
    if (targetOutsideVertical && !targetOutsideHorizontal) {
      useHorizontal = false;
    } else if (targetOutsideHorizontal && !targetOutsideVertical) {
      useHorizontal = true;
    } else {
      useHorizontal = Math.abs(diffX) >= Math.abs(diffY);
    }
    const sourcePort = (sampleEdge.getSource?.() as any)?.port;
    const isOptionArrowSource = sourcePort === 'port-arrow';
    // anchorName: direction edges ENTER the target. If sources are to the left (diffX<0), enter from 'left'.
    const anchorName: EdgeDirection = isOptionTarget ? 'top'
      : useHorizontal ? (diffX > 0 ? 'right' : 'left')
      : (diffY > 0 ? 'bottom' : 'top');

    if (remainingEdges.length <= 1) {
      if (remainingEdges[0]) {
        const data = ensureRouteMetadata(remainingEdges[0]);
        remainingEdges[0].setData({ ...data, _mergedEdge: true });
        applySingleEdgeRouting(graph, remainingEdges[0], anchorName, isOptionTarget, false);
      }
      return;
    }

    // Bus placement: OUTSIDE target on the side matching anchorName
    const isVerticalAnchor = anchorName === 'top' || anchorName === 'bottom';
    const HUB_OFFSET = Math.max(40, isVerticalAnchor ? targetSize.height / 2 + 20 : targetSize.width / 2 + 20);
    const mergePoint = isVerticalAnchor
      ? {
          x: targetCenter.x,
          y: anchorName === 'top' ? targetPos.y - HUB_OFFSET : targetPos.y + targetSize.height + HUB_OFFSET,
        }
      : {
          x: anchorName === 'left' ? targetPos.x - HUB_OFFSET : targetPos.x + targetSize.width + HUB_OFFSET,
          y: targetCenter.y,
        };

    const targetBoundary = getBoundaryPoint(targetNode, anchorName);
    const targetStub = shiftPoint(targetBoundary, anchorName, STUB_LENGTH);
    const busIsHorizontal = isVerticalAnchor;

    remainingEdges.forEach(edge => {
      const data = ensureRouteMetadata(edge);
      const targetId = edge.getTargetCellId();
      if (targetId) {
        if (isOptionTarget || isOptionArrowSource) {
          const targetConfig: any = { cell: targetId, anchor: { name: anchorName } };
          targetConfig.port = isOptionTarget ? 'port-arrow' : 'port-top';
          targetConfig.connectionPoint = { name: 'anchor' };
          edge.setTarget(targetConfig);
        } else {
          edge.setTarget({ cell: targetId, connectionPoint: { name: 'boundary' } });
        }
      }

      const sourceNode = graph.getCellById(edge.getSourceCellId()!) as Node;
      if (!sourceNode || !sourceNode.isNode()) return;

      const sp = sourceNode.getPosition();
      const ss = sourceNode.getSize();
      const sourceCenter = { x: sp.x + ss.width / 2, y: sp.y + ss.height / 2 };
      const hubPoint = { x: mergePoint.x, y: mergePoint.y };

      const desiredSourceDir: EdgeDirection = (() => {
        const deltaX = targetCenter.x - sourceCenter.x;
        const deltaY = targetCenter.y - sourceCenter.y;
        if (Math.abs(deltaX) < 6 && Math.abs(deltaY) < 6) return 'bottom';
        if (Math.abs(deltaX) >= Math.abs(deltaY)) {
          return deltaX >= 0 ? 'right' : 'left';
        }
        return deltaY >= 0 ? 'bottom' : 'top';
      })();

      const sourcePort = (edge.getSource?.() as any)?.port;
      if (sourcePort === 'port-arrow') {
        edge.setSource({
          cell: sourceNode.id,
          port: 'port-arrow',
          anchor: { name: 'top' },
          connectionPoint: { name: 'anchor' },
        });
      } else if (isOptionTarget || isOptionArrowSource) {
        edge.setSource({
          cell: sourceNode.id,
          port: `port-${desiredSourceDir}`,
          anchor: { name: desiredSourceDir },
          connectionPoint: { name: 'anchor' },
        });
      } else {
        edge.setSource({ cell: sourceNode.id });
      }

      if (sourcePort === 'port-arrow') {
        const srcBox = sourceNode.getBBox();
        const srcCx = srcBox.x + srcBox.width / 2;
        const stubY = srcBox.y - 32;
        edge.setVertices([
          { x: srcCx, y: stubY },
          { x: targetCenter.x, y: stubY },
        ]);
        edge.setRouter({ name: 'orth', args: { padding: 20 } });
        edge.setConnector({ name: 'normal' });
      } else if (isOptionTarget || isOptionArrowSource) {
        const sourceBoundary = getBoundaryPoint(sourceNode, desiredSourceDir);
        const sourceStub = shiftPoint(sourceBoundary, desiredSourceDir, STUB_LENGTH);
        const vertices: { x: number; y: number }[] = [sourceStub];
        const pushCorner = (pt: { x: number; y: number }) => {
          const last = vertices[vertices.length - 1];
          if (!last || Math.abs(last.x - pt.x) > 0.5 || Math.abs(last.y - pt.y) > 0.5) {
            vertices.push(pt);
          }
        };

        if (busIsHorizontal) {
          const busY = mergePoint.y;
          pushCorner({ x: sourceStub.x, y: busY });
          pushCorner({ x: mergePoint.x, y: busY });
          pushCorner({ x: targetStub.x, y: busY });
        } else {
          const busX = mergePoint.x;
          pushCorner({ x: busX, y: sourceStub.y });
          pushCorner({ x: busX, y: mergePoint.y });
          pushCorner({ x: busX, y: targetStub.y });
        }
        pushCorner(targetStub);

        edge.setRouter({ name: 'normal' });
        edge.setConnector({ name: 'normal' });
        edge.setVertices(vertices);
      } else {
        edge.setVertices([]);
        edge.setRouter({ name: 'orth', args: { padding: 20 } });
        edge.setConnector({ name: 'normal' });
      }

      if (!data._mergedEdge) {
        edge.setData({ ...data, _mergedEdge: true });
      }
    });
  });
}

function rawEdgeSubtype(edge: Edge, data: EdgeRoutingMeta): string {
  return (
    data.edgeSubtype ||
    ((edge.getData?.() as any)?.edgeSubtype as string) ||
    ''
  );
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const nodeCenter = (node: Node) => {
  const bbox = node.getBBox();
  return {
    x: bbox.x + bbox.width / 2,
    y: bbox.y + bbox.height / 2,
  };
};

const parentBoundsWithPadding = (parent: Node, padding: number) => {
  const bbox = parent.getBBox();
  return {
    minX: bbox.x + padding,
    maxX: bbox.x + bbox.width - padding,
    minY: bbox.y + padding,
    maxY: bbox.y + bbox.height - padding,
  };
};

const nudgePointInside = (point: { x: number; y: number }, dir: EdgeDirection, inset: number) => {
  switch (dir) {
    case 'left':
      return { x: point.x + inset, y: point.y };
    case 'right':
      return { x: point.x - inset, y: point.y };
    case 'top':
      return { x: point.x, y: point.y + inset };
    case 'bottom':
    default:
      return { x: point.x, y: point.y - inset };
  }
};

export function layoutSiblingEdgeWithinParent(
  edge: Edge,
  sourceNode: Node,
  targetNode: Node,
  parentNode: Node,
) {
  const bounds = parentBoundsWithPadding(parentNode, 24);
  const srcCenter = nodeCenter(sourceNode);
  const tgtCenter = nodeCenter(targetNode);
  const dx = tgtCenter.x - srcCenter.x;
  const dy = tgtCenter.y - srcCenter.y;
  const horizontalFirst = Math.abs(dx) >= Math.abs(dy);

  const startDir: EdgeDirection = horizontalFirst
    ? dx >= 0 ? 'right' : 'left'
    : dy >= 0 ? 'bottom' : 'top';
  const endDir: EdgeDirection = horizontalFirst
    ? dx >= 0 ? 'left' : 'right'
    : dy >= 0 ? 'top' : 'bottom';

  const startPoint = getBoundaryPoint(sourceNode, startDir);
  const endPoint = getBoundaryPoint(targetNode, endDir);

  const inset = 18;
  const startInside = nudgePointInside(startPoint, startDir, inset);
  startInside.x = clamp(startInside.x, bounds.minX, bounds.maxX);
  startInside.y = clamp(startInside.y, bounds.minY, bounds.maxY);
  const endClamped = { x: clamp(endPoint.x, bounds.minX, bounds.maxX), y: clamp(endPoint.y, bounds.minY, bounds.maxY) };

  edge.setSource({ cell: sourceNode.id, connectionPoint: { name: 'boundary' } });
  edge.setTarget({ cell: targetNode.id, connectionPoint: { name: 'boundary' } });

  let vertices: { x: number; y: number }[];
  if (horizontalFirst) {
    const midX = clamp((startPoint.x + endPoint.x) / 2, bounds.minX, bounds.maxX);
    vertices = [
      startInside,
      { x: midX, y: startInside.y },
      { x: midX, y: endClamped.y },
    ];
  } else {
    const midY = clamp((startPoint.y + endPoint.y) / 2, bounds.minY, bounds.maxY);
    vertices = [
      startInside,
      { x: startInside.x, y: midY },
      { x: endClamped.x, y: midY },
    ];
  }

  edge.setVertices(vertices);
  edge.setRouter({ name: 'orth', args: { padding: 4 } });
  edge.setConnector('normal');

  const srcZ = sourceNode.getZIndex?.();
  const tgtZ = targetNode.getZIndex?.();
  const parentZ = parentNode.getZIndex?.();
  const maxNodeZ = Math.max(
    typeof srcZ === 'number' ? srcZ : 0,
    typeof tgtZ === 'number' ? tgtZ : 0,
    typeof parentZ === 'number' ? parentZ : 0,
  );
  if (edge.setZIndex) {
    edge.setZIndex(maxNodeZ + 10);
  }
}

export function layoutChildToParentEdgeWithinParent(
  edge: Edge,
  childNode: Node,
  parentNode: Node,
  attachPoint?: { x: number; y: number },
) {
  const bounds = parentBoundsWithPadding(parentNode, 24);
  const childCenter = nodeCenter(childNode);
  const edgeData = (edge.getData?.() || {}) as EdgeRoutingMeta;

  let attach =
    edgeData._parentAttachSide && typeof edgeData._parentAttachRatio === 'number'
      ? { side: edgeData._parentAttachSide, ratio: edgeData._parentAttachRatio }
      : null;

  if (attachPoint) {
    attach = toParentAttach(parentNode, attachPoint);
  }

  if (!attach) {
    const parentCenter = nodeCenter(parentNode);
    const dxCenter = parentCenter.x - childCenter.x;
    const dyCenter = parentCenter.y - childCenter.y;
    const horizontalFirstCenter = Math.abs(dxCenter) >= Math.abs(dyCenter);
    const autoSide: EdgeDirection = horizontalFirstCenter
      ? dxCenter >= 0 ? 'left' : 'right'
      : dyCenter >= 0 ? 'top' : 'bottom';
    attach = { side: autoSide, ratio: 0.5 };
  }

  if (
    edgeData._parentAttachSide !== attach.side ||
    typeof edgeData._parentAttachRatio !== 'number' ||
    Math.abs(edgeData._parentAttachRatio - attach.ratio) > 1e-4
  ) {
    edge.setData({
      ...edgeData,
      _parentAttachSide: attach.side,
      _parentAttachRatio: attach.ratio,
    });
  }

  const parentBoundary = pointFromParentAttach(parentNode, attach.side, attach.ratio);
  const dx = parentBoundary.x - childCenter.x;
  const dy = parentBoundary.y - childCenter.y;
  const horizontalFirst = Math.abs(dx) >= Math.abs(dy);

  const childDir: EdgeDirection = horizontalFirst
    ? dx >= 0 ? 'right' : 'left'
    : dy >= 0 ? 'bottom' : 'top';

  const childBoundary = getBoundaryPoint(childNode, childDir);
  const inset = 18;
  const childInside = nudgePointInside(childBoundary, childDir, inset);
  childInside.x = clamp(childInside.x, bounds.minX, bounds.maxX);
  childInside.y = clamp(childInside.y, bounds.minY, bounds.maxY);

  const parentInside = nudgePointInside(parentBoundary, attach.side, inset);
  parentInside.x = clamp(parentInside.x, bounds.minX, bounds.maxX);
  parentInside.y = clamp(parentInside.y, bounds.minY, bounds.maxY);

  let vertices: { x: number; y: number }[];
  if (horizontalFirst) {
    const midX = clamp((childInside.x + parentInside.x) / 2, bounds.minX, bounds.maxX);
    vertices = [
      childInside,
      { x: midX, y: childInside.y },
      { x: midX, y: parentInside.y },
      parentInside,
    ];
  } else {
    const midY = clamp((childInside.y + parentInside.y) / 2, bounds.minY, bounds.maxY);
    vertices = [
      childInside,
      { x: childInside.x, y: midY },
      { x: parentInside.x, y: midY },
      parentInside,
    ];
  }

  edge.setVertices(vertices);
  edge.setRouter({ name: 'orth', args: { padding: 4 } });
  edge.setConnector('normal');

  const childZ = childNode.getZIndex?.();
  const parentZ = parentNode.getZIndex?.();
  const maxNodeZ = Math.max(
    typeof childZ === 'number' ? childZ : 0,
    typeof parentZ === 'number' ? parentZ : 0,
  );
  if (edge.setZIndex) {
    edge.setZIndex(maxNodeZ + 10);
  }
}
